// Endpoints go through these helpers instead of inlining `{ type, id }`
// literals so providing and invalidating sides stay aligned on the id
// format.

export const applicationsListTag = {
  type: 'Applications' as const,
  id: 'LIST',
};

export const applicationTag = (appId: string) => ({
  type: 'Applications' as const,
  id: appId,
});

export const applicationVersionsByAppTag = (appId: string) => ({
  type: 'ApplicationVersions' as const,
  id: appId,
});

export const applicationVersionTag = (appId: string, versionId: string) => ({
  type: 'ApplicationVersions' as const,
  id: `${appId}/${versionId}`,
});

export const applicationVersionAssetTag = (
  appId: string,
  versionId: string,
  assetId: string,
) => ({
  type: 'ApplicationVersionAssets' as const,
  id: `${appId}/${versionId}/${assetId}`,
});

export const applicationVersionAssetsListTag = (
  appId: string,
  versionId: string,
) => ({
  type: 'ApplicationVersionAssets' as const,
  id: `${appId}/${versionId}`,
});

export const applicationVersionContentsTag = (
  appId: string,
  versionId: string,
) => ({
  type: 'ApplicationVersionContents' as const,
  id: `${appId}/${versionId}`,
});

export const applicationVersionPackagesSummaryTag = (
  appId: string,
  versionId: string,
) => ({
  type: 'ApplicationVersionPackagesSummary' as const,
  id: `${appId}/${versionId}`,
});

export const applicationVersionVulnerabilitiesTag = (
  appId: string,
  versionId: string,
) => ({
  type: 'ApplicationVersionVulnerabilities' as const,
  id: `${appId}/${versionId}`,
});

export const applicationVersionVulnerabilitySummaryTag = (
  appId: string,
  versionId: string,
) => ({
  type: 'ApplicationVersionVulnerabilitySummary' as const,
  id: `${appId}/${versionId}`,
});

export const applicationVersionVulnerabilityPackagesTag = (
  appId: string,
  versionId: string,
  vulnerabilityId: string,
) => ({
  type: 'ApplicationVersionVulnerabilityPackages' as const,
  id: `${appId}/${versionId}/${vulnerabilityId}`,
});

// Type-only sweep — used when an asset-ingress completion may
// affect packages across vulns we can't enumerate up front.
export const applicationVersionVulnerabilityPackagesSweepTag = {
  type: 'ApplicationVersionVulnerabilityPackages' as const,
};

export const applicationVersionVulnerabilityAnnotationTag = (
  appId: string,
  versionId: string,
  annotationId: string,
) => ({
  type: 'ApplicationVersionVulnerabilityAnnotations' as const,
  id: `${appId}/${versionId}/${annotationId}`,
});

export const applicationVersionPolicyTag = (
  appId: string,
  versionId: string,
) => ({
  type: 'ApplicationVersionPolicy' as const,
  id: `${appId}/${versionId}`,
});

export const applicationVersionPolicyByAppTag = (appId: string) => ({
  type: 'ApplicationVersionPolicy' as const,
  id: appId,
});

export const applicationJobsListByAppTag = (appId: string) => ({
  type: 'ApplicationJobs' as const,
  id: `LIST/${appId}`,
});

export const applicationJobTag = (appId: string, jobId: string) => ({
  type: 'ApplicationJobs' as const,
  id: `${appId}/${jobId}`,
});

/**
 * Every cache derived from a version's asset set, plus the parent
 * application (whose `latestVersion` carries derived counts).
 */
export const applicationVersionScopedTags = (
  appId: string,
  versionId: string,
) => [
  applicationTag(appId),
  applicationVersionTag(appId, versionId),
  applicationVersionAssetsListTag(appId, versionId),
  applicationVersionContentsTag(appId, versionId),
  applicationVersionPackagesSummaryTag(appId, versionId),
  applicationVersionVulnerabilitiesTag(appId, versionId),
  applicationVersionVulnerabilitySummaryTag(appId, versionId),
  applicationVersionVulnerabilityPackagesSweepTag,
  applicationVersionPolicyTag(appId, versionId),
];
